import {createWebHashHistory, createRouter} from "vue-router"
import { useUserStore } from '@/stores/user'
import { ElMessage } from 'element-plus'

const routes = [
  {
    // 门户首页（根路径，不含 /portal）
    path: '/',
    name: 'portal',
    component: () => import('@/page/portal/index.vue')
  },
  {
    path: '/features',
    name: 'portal-features',
    component: () => import('@/page/portal/features.vue')
  },
  {
    path: '/solutions',
    name: 'portal-solutions',
    component: () => import('@/page/portal/solutions.vue')
  },
  {
    path: '/about',
    name: 'portal-about',
    component: () => import('@/page/portal/about.vue')
  },
  // 旧 /portal 路径兼容重定向
  { path: '/portal', redirect: '/' },
  { path: '/portal/features', redirect: '/features' },
  { path: '/portal/solutions', redirect: '/solutions' },
  { path: '/portal/about', redirect: '/about' },
  {
    // 我的控制台（登录后工作台首页）
    path: '/console',
    name: 'console',
    component: () => import('@/page/console/index.vue'),
    meta: { requiresAuth: true, hideTopNav: true }
  },
  {
    // AI 对话（原根路径，现改为 /chat；路由名仍为 home 以兼容各处 push）
    path: '/chat',
    name: 'home',
    component: () => import('@/page/home/index.vue'),
    meta: { requiresAuth: true, hideTopNav: true }
  },
  {
    // AI 绘画（控制台顶部进入）
    path: '/draw',
    name: 'ai-draw',
    component: () => import('@/page/ai-draw/index.vue'),
    meta: { requiresAuth: true, hideTopNav: true }
  },
  {
    // AI 工作流（控制台顶部进入）
    path: '/workflow',
    name: 'ai-workflow',
    component: () => import('@/page/ai-workflow/index.vue'),
    meta: { requiresAuth: true, hideTopNav: true }
  },
  {
    // 对话角色管理（原提示词库，权限名仍为 prompts）
    path: '/prompts',
    name: 'prompts',
    component: () => import('@/page/prompts/index.vue'),
    meta: { requiresAuth: true, requiresPermission: 'prompts' }
  },
  {
    // 知识库
    path: '/knowledge',
    name: 'knowledge',
    component: () => import('@/page/knowledge/index.vue'),
    meta: { requiresAuth: true, requiresPermission: 'knowledge' }
  },
  {
    // 知识库创建向导
    path: '/knowledge/create',
    name: 'knowledge-create',
    component: () => import('@/page/knowledge/create.vue'),
    meta: { requiresAuth: true, requiresPermission: 'knowledge' }
  },
  {
    // 知识库文档列表
    path: '/knowledge/:kbId/documents',
    name: 'knowledge-documents',
    component: () => import('@/page/knowledge/documents.vue'),
    meta: { requiresAuth: true, requiresPermission: 'knowledge' }
  },
  {
    // 文档分块详情
    path: '/documents/:docId/chunks',
    name: 'document-chunks',
    component: () => import('@/page/knowledge/chunks.vue'),
    meta: { requiresAuth: true, requiresPermission: 'knowledge' }
  },
  {
    // 登录页面
    path: '/login',
    name: 'login',
    component: () => import('@/page/auth/login.vue'),
    meta: { requiresGuest: true }
  },
  {
    // 注册页面
    path: '/register',
    name: 'register',
    component: () => import('@/page/auth/register.vue'),
    meta: { requiresGuest: true }
  },
  {
    // 用户设置页面
    path: '/settings',
    name: 'settings',
    component: () => import('@/page/user/settings.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/settings/draw-styles',
    redirect: '/draw-styles'
  },
  {
    // AI 绘画风格管理（需绘画风格管理权限，与对话角色管理独立）
    path: '/draw-styles',
    name: 'draw-styles-settings',
    component: () => import('@/page/user/draw-styles-settings.vue'),
    meta: { requiresAuth: true, requiresPermission: 'draw_styles' }
  },
  {
    // 管理员页面
    path: '/admin',
    name: 'admin',
    component: () => import('@/page/admin/index.vue'),
    meta: { requiresAuth: true, requiresPermission: 'user_management' }
  }
]

//创建路由实例
const router = createRouter({
  history: createWebHashHistory(),
  routes: routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth'
      }
    }
    return { top: 0 }
  }
})

// 路由守卫
router.beforeEach((to, from, next) => {
  const userStore = useUserStore()
  
  // 需要登录的页面
  if (to.meta.requiresAuth) {
    if (!userStore.isLoggedIn) {
      ElMessage.warning('请先登录')
      next({ name: 'login', query: { redirect: to.fullPath } })
      return
    }
    
    // 检查权限
    if (to.meta.requiresPermission) {
      if (!userStore.hasPermission(to.meta.requiresPermission)) {
        ElMessage.error('权限不足，无法访问该页面')
        // 跳转到用户有权限的默认页面
        if (userStore.hasPermission('prompts')) {
          next({ name: 'prompts' })
        } else if (userStore.hasPermission('draw_styles')) {
          next({ name: 'draw-styles-settings' })
        } else if (userStore.hasPermission('knowledge')) {
          next({ name: 'knowledge' })
        } else {
          // AI 对话不再做权限校验，兜底回到对话页
          next({ name: 'home' })
        }
        return
      }
    }
  }
  
  // 只允许游客访问的页面（如登录、注册）
  if (to.meta.requiresGuest && userStore.isLoggedIn) {
    next({ name: 'portal' })
    return
  }
  
  next()
})

export default router;
